import styled from "@emotion/styled";
import { FormProvider, useForm } from "react-hook-form";
import { useEffect } from "react";
import { postKmsData, translate } from "@kaltura/mediaspace-shared-utils";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts";
import { RichtextField } from "@kaltura/mediaspace-shared-ui/controlled-form";
import { NameField } from "@kaltura/mediaspace-shared-ui";

const StyledForm = styled("form")(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
}));

type CreatePlaylistResponse = {
    success: boolean;
    redirectUrl?: string;
    errorMessage?: string;
};

export interface CreatePlaylistFormProps {
    /**
     * Form ID to be used for form submission
     */
    formId: string;

    /**
     * Function to update parent's processing state
     */
    updateProcessing: (value: boolean) => void;

    /**
     * Function to update parent's disable button state
     */
    updateDisableButton: (value: boolean) => void;

    /**
     * API URL for creating the playlist
     */
    apiUrl: string;
}

const baseClassName = "kms-ds-create-playlist-form";

/**
 * Form for creating a new playlist
 */
const CreatePlaylistForm = (props: CreatePlaylistFormProps) => {
    const { formId, updateProcessing, updateDisableButton, apiUrl } = props;
    const sendButtonAnalytics = useButtonAnalytics();
    const { showToast } = useToastsContext();

    const useFormHookMethods = useForm({
        mode: "onChange",
        defaultValues: {
            name: "",
            description: "",
        },
    });

    // Update parent component when form validation state changes
    useEffect(() => {
        updateDisableButton(!useFormHookMethods.formState.isValid);
    }, [useFormHookMethods.formState.isValid, updateDisableButton]);

    const onSubmit = async (data: { [key: string]: string | boolean }) => {
        updateProcessing(true);

        try {
            const result = (await postKmsData(apiUrl, data)) as CreatePlaylistResponse;
            if (result.success && result.redirectUrl) {
                // redirect to the new playlist edit page on success
                sendButtonAnalytics(
                    "Create playlist - DS",
                    ButtonClickAnalyticsType.CREATE,
                    undefined,
                    "Addtoplaylists",
                    "Modal box - Create playlist"
                );
                window.location.href = result.redirectUrl;
            }
            if (!result.success) {
                // show toast in case of other errors
                showToast({ severity: "error", message: result.errorMessage });
                updateProcessing(false);
            }
        }
        catch (e) {
            updateProcessing(false);
            showToast({ severity: "error", message: translate("Failed to create playlist") });
        }
    };

    return (
        <FormProvider {...useFormHookMethods}>
            <StyledForm id={formId} className={baseClassName} onSubmit={useFormHookMethods.handleSubmit(onSubmit)}>
                <NameField id={"name"} label={translate("Name")} className={`${baseClassName}-name`} />
                <RichtextField
                    label={translate("Description")}
                    id={"description"}
                    fontWeightBold={true}
                    inline={true}
                />
            </StyledForm>
        </FormProvider>
    );
};

export default CreatePlaylistForm;
